import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, DollarSign, Shield, Clock, ThumbsUp, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WhyUsSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  const features = [
    {
      icon: MapPin,
      title: t('whyUs.feature1.title'),
      desc: t('whyUs.feature1.description'),
      accent: true,
    },
    {
      icon: DollarSign,
      title: t('whyUs.feature2.title'),
      desc: t('whyUs.feature2.description'),
    },
    {
      icon: Shield,
      title: t('whyUs.feature3.title'),
      desc: t('whyUs.feature3.description'),
    },
    {
      icon: Clock,
      title: t('whyUs.feature4.title'),
      desc: t('whyUs.feature4.description'),
    },
    {
      icon: ThumbsUp,
      title: t('whyUs.feature5.title'),
      desc: t('whyUs.feature5.description'),
    },
    {
      icon: Star,
      title: t('whyUs.feature6.title'),
      desc: t('whyUs.feature6.description'),
    },
  ];

  return (
    <section id="why-us" ref={ref} className="py-16 sm:py-28 relative noise-overlay" style={{ background: 'linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #1e40af 100%)' }}>
      <div className="gold-line mb-10 sm:mb-16" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 sm:mb-20"
        >
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black mb-4 sm:mb-5 text-white">
            {t('whyUs.title')}
          </h2>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            {t('whyUs.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-4 sm:space-y-6">
          {/* Featured card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div
              className="relative p-6 sm:p-10 lg:p-14 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.45) 0%, rgba(96, 165, 250, 0.08) 100%)',
                border: '2px solid rgba(96, 165, 250, 0.3)',
                borderRadius: '1.5rem',
                boxShadow: '0 10px 40px rgba(96, 165, 250, 0.15)',
              }}
            >
              <div className="hidden sm:block absolute top-0 left-0 w-60 h-60 opacity-[0.08] blob-shape" style={{ background: '#60a5fa', filter: 'blur(60px)' }} />
              <div className="relative z-10 flex flex-col sm:flex-row items-center gap-5 sm:gap-10">
                <div
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(96, 165, 250, 0.05))',
                    border: '1px solid rgba(96, 165, 250, 0.3)',
                  }}
                >
                  <MapPin size={28} style={{ color: '#6ee7b7' }} />
                </div>
                <div className="text-center sm:text-right flex-1">
                  <div className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-2 sm:mb-3" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8d48b)', color: '#1e3a8a', boxShadow: '0 2px 10px rgba(201, 168, 76, 0.3)' }}>
                    {t('whyUs.mainAdvantage')}
                  </div>
                  <h3 className="text-xl sm:text-3xl font-black text-white mb-2 sm:mb-3">{features[0].title}</h3>
                  <p className="text-blue-100 text-sm sm:text-base leading-relaxed">
                    {features[0].desc}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Other features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {features.slice(1).map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.08, duration: 0.5 }}
              >
                <div
                  className="flex items-start gap-4 p-5 sm:p-7 h-full transition-all duration-400"
                  style={{
                    background: 'rgba(30, 64, 175, 0.3)',
                    border: '1px solid rgba(96, 165, 250, 0.2)',
                    borderRadius: '1.2rem',
                    boxShadow: '0 4px 15px rgba(96, 165, 250, 0.08)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
                    e.currentTarget.style.background = 'rgba(37, 99, 235, 0.45)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(96, 165, 250, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.2)';
                    e.currentTarget.style.background = 'rgba(30, 64, 175, 0.3)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(96, 165, 250, 0.08)';
                  }}
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #c9a84c, #e8d48b)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 2px 10px rgba(201, 168, 76, 0.2)',
                    }}
                  >
                    <feature.icon size={20} style={{ color: '#1e3a8a' }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">{feature.title}</h3>
                    <p className="text-blue-100 text-xs sm:text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24" style={{ background: 'linear-gradient(to top, #1e3a8a, transparent)' }} />
    </section>
  );
};

export default WhyUsSection;
